import type { ReactNode } from 'react';
import { Navigate, useMatches } from 'react-router-dom';

import { homePathForLevel } from '@/app/navigation-context/navigation-context.utils.js';
import type { NavigationLevel } from '@/app/navigation-context/types.js';
import { useNavigationContext } from '@/app/navigation-context/use-navigation-context.js';
import { appPaths } from '@/app/router/paths.js';

export interface AppRouteHandle {
  navigationLevel?: NavigationLevel | NavigationLevel[];
}

interface NavigationLevelGuardProps {
  children: ReactNode;
}

function resolveRequiredLevels(matches: ReturnType<typeof useMatches>): NavigationLevel[] | null {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index]?.handle as AppRouteHandle | undefined;
    if (handle?.navigationLevel) {
      return Array.isArray(handle.navigationLevel)
        ? handle.navigationLevel
        : [handle.navigationLevel];
    }
  }
  return null;
}

export function NavigationLevelGuard({ children }: NavigationLevelGuardProps) {
  const matches = useMatches();
  const { level, isReady, hasPlatformAccess } = useNavigationContext();
  const requiredLevels = resolveRequiredLevels(matches);

  if (!isReady) {
    return null;
  }

  if (!requiredLevels) {
    return children;
  }

  if (requiredLevels.includes(level)) {
    return children;
  }

  if (requiredLevels.includes('platform') && !hasPlatformAccess) {
    return <Navigate to={appPaths.home} replace />;
  }

  return <Navigate to={homePathForLevel(level, null)} replace />;
}
